import React from 'react';
import { 
  FileCheck, 
  Download, 
  RefreshCw
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import { useAdminReports } from '../../hooks/useAdminReports';

const AdminReports = () => {
  // Use the custom hook for reports data
  const {
    report,
    isLoading,
    error,
    reportType,
    setReportType,
    reportTypeOptions,
    period,
    setPeriod,
    periodOptions,
    refetch
  } = useAdminReports('bookings', 'month');

  const handleDownload = () => {
    // TODO: Implement CSV/PDF download
    console.log('Download report:', report);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <p className="text-red-600">Failed to load report. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileCheck className="w-6 h-6" />
                Reports
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Generate and view detailed platform reports
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SearchSelect
              label="Report Type"
              value={reportType}
              onChange={setReportType}
              options={reportTypeOptions}
            />
            <SearchSelect
              label="Period"
              value={period}
              onChange={setPeriod}
              options={periodOptions}
            />
            <button
              onClick={() => refetch()}
              className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white border rounded-lg p-6">
            <div className="text-center">Loading report...</div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Summary Cards */}
            {report.summary && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {Object.entries(report.summary).slice(0, 4).map(([key, value]) => (
                  <div key={key} className="bg-white border rounded-lg p-3 sm:p-4">
                    <p className="text-sm text-gray-600 capitalize">
                      {key.replace(/_/g, ' ')}
                    </p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {typeof value === 'number' 
                        ? (key.includes('revenue') || key.includes('amount') || key.includes('total'))
                          ? `$${value.toFixed(2)}`
                          : value.toLocaleString()
                        : value}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Report Details */}
            <div className="bg-white border rounded-lg p-3 sm:p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Report Summary</h2>
              <div className="space-y-4">
                {report.summary && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Summary</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {Object.entries(report.summary).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <span className="text-xs text-gray-600 capitalize">
                            {key.replace(/_/g, ' ')}
                          </span>
                          <span className="text-sm font-medium">
                            {typeof value === 'number' 
                              ? (key.includes('revenue') || key.includes('amount') || key.includes('total'))
                                ? `$${value.toFixed(2)}`
                                : value.toLocaleString()
                              : value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* By Status */}
                {report.by_status && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">By Status</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {Object.entries(report.by_status).map(([status, count]) => (
                        <div key={status} className="p-2 bg-gray-50 rounded">
                          <span className="text-xs text-gray-600 capitalize">{status}</span>
                          <p className="text-lg font-bold">{count}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* By Month */}
                {report.by_month && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">By Month</h3>
                    <div className="space-y-1">
                      {Object.entries(report.by_month).map(([month, value]) => (
                        <div key={month} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <span className="text-sm text-gray-600">{month}</span>
                          <span className="text-sm font-medium">
                            {typeof value === 'number' 
                              ? (reportType === 'revenue' || reportType === 'bookings')
                                ? reportType === 'revenue' 
                                  ? `$${value.toFixed(2)}`
                                  : value.toLocaleString()
                                : value.toLocaleString()
                              : value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReports;

